import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { propertyService } from '../../services/propertyService';
import { uploadImage, } from '../../services/imageUpload';
import { XCircle, Image as ImageIcon, Loader2 } from 'lucide-react';
import { propertyValidationSchema } from '../../validation/propertyValidationSchema';
import { ZodError } from 'zod';

function PropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    property_type: 'house',
    status: 'available',
    amenities: '',
    address: '',
    city: '',
    state: '',
    parking: '',
    year_built: '',
    lat: '',
    lng: '',
    images: [],
    featured: false 
  });

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  useEffect(() => {
    return () => {
      formData.images.forEach(image => {
        if (image.isLocal && image.url) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, [formData.images]);

  const loadProperty = async () => {
    try {
      const property = await propertyService.getById(id);
      if (property) {
        setFormData(prev => ({
          ...prev,
          ...property,
          title: property.title || '',
          description: property.description || '',
          price: property.price || '',
          location: property.location || '',
          bedrooms: property.bedrooms || '',
          bathrooms: property.bathrooms || '',
          area: property.area || '',
          property_type: property.property_type || 'house',
          status: property.status || 'available',
          amenities: Array.isArray(property.amenities) ? property.amenities.join(', ') : property.amenities || '',
          address: property.address || '',
          city: property.city || '',
          state: property.state || '',
          parking: property.parking || '',
          year_built: property.year_built || '',
          lat: property.lat || '',
          lng: property.lng || '',
          images: Array.isArray(property.images)
            ? property.images.map(img => ({ url: img.url || img, isLocal: false, file: null }))
            : [],
          featured: property.featured || false // Added 'featured' field
        }));
      }
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Failed to load property details.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (formData.images.length + files.length > 10) {
      toast.error('You can upload a maximum of 10 images.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploadingImages(true);
    let newImagePreviews = [];
    const uploadPromises = [];

    files.forEach(file => {
      const localUrl = URL.createObjectURL(file);
      newImagePreviews.push({ url: localUrl, isLocal: true, file: file, name: file.name });

      uploadPromises.push(
        uploadImage(file)
          .then(uploadedUrl => ({ url: uploadedUrl, isLocal: false, file: file, name: file.name }))
          .catch(error => {
            toast.error(`Failed to upload ${file.name}: ${error.message}`);
            URL.revokeObjectURL(localUrl);
            return null;
          })
      );
    });

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImagePreviews]
    }));
    if (errors.images) {
      setErrors(prev => ({ ...prev, images: undefined }));
    }


    const uploadedResults = await Promise.all(uploadPromises);
    setIsUploadingImages(false);

    setFormData(prev => {
      const currentImages = [...prev.images];
      const finalImages = currentImages.map(img => {
        const matchedUpload = uploadedResults.find(
          (uploadedImg) => uploadedImg && uploadedImg.file === img.file
        );

        if (matchedUpload) {
          if (img.isLocal && img.url) {
            URL.revokeObjectURL(img.url);
          }
          return { url: matchedUpload.url, isLocal: false, file: null };
        } else if (img.isLocal && img.file && !uploadedResults.some(res => res && res.file === img.file)) {
          URL.revokeObjectURL(img.url);
          return null;
        }
        return img;
      }).filter(Boolean);

      return {
        ...prev,
        images: finalImages
      };
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => {
      const updatedImages = prev.images.filter((img, index) => {
        if (index === indexToRemove) {
          if (img.isLocal && img.url) {
            URL.revokeObjectURL(img.url);
          }
          return false;
        }
        return true;
      });
      if (updatedImages.length === 0) {
        setErrors(prev => ({ ...prev, images: 'At least one image is required.' }));
      }
      return { ...prev, images: updatedImages };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const parsedData = {
        ...formData,
        price: formData.price !== '' ? Number(formData.price) : undefined,
        bedrooms: formData.bedrooms !== '' ? Number(formData.bedrooms) : undefined,
        bathrooms: formData.bathrooms !== '' ? Number(formData.bathrooms) : undefined,
        area: formData.area !== '' ? Number(formData.area) : undefined,
        parking: formData.parking !== '' ? Number(formData.parking) : undefined,
        year_built: formData.year_built !== '' ? Number(formData.year_built) : undefined,
        lat: formData.lat !== '' ? Number(formData.lat) : undefined,
        lng: formData.lng !== '' ? Number(formData.lng) : undefined,
        amenities: typeof formData.amenities === 'string'
          ? formData.amenities.split(',').map(a => a.trim()).filter(Boolean).join(', ')
          : '',
        images: formData.images.filter(img => !img.isLocal)
      };

      // Zod validation
      await propertyValidationSchema.parseAsync(parsedData);

      const dataForSubmission = {
        ...parsedData,
        amenities: JSON.stringify(
          typeof parsedData.amenities === 'string'
            ? parsedData.amenities.split(',').map(a => a.trim()).filter(Boolean)
            : []
        ),
        images: parsedData.images.map(img => ({ url: img.url }))
      };

      if (id) {
        await propertyService.update(id, dataForSubmission);
        toast.success('Property updated successfully!');
      } else {
        await propertyService.create(dataForSubmission);
        toast.success('Property created successfully!');
      }

      navigate('/aadashmin/properties');
    } catch (error) {
      console.error('Error saving property:', error);
      if (error instanceof ZodError) {
        const fieldErrors = {};
        error.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path[0]] = err.message;
          }
        });
        setErrors(fieldErrors);
        toast.error('Please correct the highlighted errors.');
      } else {
        toast.error(`Failed to save property: ${error.message || 'An unknown error occurred.'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (fieldName) => {
    return errors[fieldName] && <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>;
  };

  return (
    <div className="w-full p-0 sm:p-6 lg:p-10 sm:max-w-6xl sm:mx-auto bg-white rounded-none sm:rounded-xl shadow-none sm:shadow-2xl border-none sm:border sm:border-gray-100 animate-fade-in-up dark:bg-gray-900 dark:border-gray-700 dark:shadow-none">
      {/* Header Section */}
      <div className="mb-6 pb-4 md:mb-8 md:pb-6 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-0">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight dark:text-white">
          {id ? 'Edit Property Listing' : 'Add New Property Listing'}
        </h2>
        <p className="mt-2 text-base sm:text-lg text-gray-600 dark:text-gray-300">
          Craft a compelling listing for your real estate portfolio.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 md:space-y-10">
        <section className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-xl border border-gray-200 shadow-lg dark:from-gray-800 dark:to-gray-900 dark:border-gray-700 dark:shadow-none mx-0 sm:mx-auto sm:max-w-6xl">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 dark:text-white">Property Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
            {[
              { name: 'title', label: 'Property Title', placeholder: 'e.g., Spacious Family Home', type: 'text', required: true },
              { name: 'price', label: 'Price (₹)', type: 'number', placeholder: 'e.g., 5,000,000', min: "0", required: true },
              { name: 'location', label: 'Main Location (Area)', placeholder: 'e.g., Banjara Hills', type: 'text', required: true },
              { name: 'bedrooms', label: 'Bedrooms', type: 'number', placeholder: 'e.g., 4', min: "0" },
              { name: 'bathrooms', label: 'Bathrooms', type: 'number', placeholder: 'e.g., 3', min: "0" },
              { name: 'area', label: 'Area (Sq. Ft.)', type: 'number', placeholder: 'e.g., 3000', min: "0" },
              { name: 'parking', label: 'Parking Slots', type: 'number', placeholder: 'e.g., 2', min: "0" },
              { name: 'year_built', label: 'Year Built', type: 'number', placeholder: 'e.g., 2010', min: "1800", max: new Date().getFullYear().toString() }
            ].map(({ name, label, type, placeholder, min, max, required }) => (
              <div key={name}>
                <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">
                  {label} {required && <span className="text-red-500">*</span>}
                </label>
                <input
                  id={name}
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  min={min}
                  max={max}
                  className={`w-full px-3 py-2 border ${errors[name] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm
                                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                                  transition-all duration-200 text-gray-800 placeholder-gray-400 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500`}
                  required={required}
                />
                {renderError(name)}
              </div>
            ))}

            {/* Property Type */}
            <div>
              <label htmlFor="property_type" className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">
                Property Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="property_type"
                  name="property_type"
                  value={formData.property_type}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.property_type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm bg-white appearance-none pr-10
                                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                                  transition-all duration-200 text-gray-800 dark:bg-gray-700 dark:text-white`}
                >
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="condo">Condo</option>
                  <option value="land">Land</option>
                  <option value="villa">Villa</option>
                  <option value="studio">Studio</option>
                  <option value="duplex">Duplex</option>
                  <option value="commercial">Commercial</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                </div>
              </div>
              {renderError('property_type')}
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">
                Status <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border ${errors.status ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm bg-white appearance-none pr-10
                                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                                  transition-all duration-200 text-gray-800 dark:bg-gray-700 dark:text-white`}
                >
                  <option value="available">Available</option>
                  <option value="pending">Pending</option>
                  <option value="sold">Sold</option>
                  <option value="rented">Rented</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                </div>
              </div>
              {renderError('status')}
            </div>
            
            {/* Featured Property */}
            <div>
              <label htmlFor="featured" className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">
                Featured Property
              </label>
              <div className="flex items-center">
                <input
                  id="featured"
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-5 h-5 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="featured" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Mark as Featured
                </label>
              </div>
            </div>

          </div>

          {/* Description */}
          <div className="mt-6">
            <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">Detailed Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              placeholder="Provide a comprehensive and engaging description of the property, highlighting its unique features, benefits, and neighborhood advantages."
              className={`w-full px-3 py-2 border ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm
                               focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                               transition-all duration-200 text-gray-800 placeholder-gray-400 resize-y dark:bg-gray-700 dark:text-white dark:placeholder-gray-500`}
            />
            {renderError('description')}
          </div>

          {/* Amenities */}
          <div className="mt-6">
            <label htmlFor="amenities" className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">Amenities (comma separated)</label>
            <input
              id="amenities"
              type="text"
              name="amenities"
              value={formData.amenities}
              onChange={handleChange}
              placeholder="e.g., Gym, Swimming Pool, 24/7 Security, Balcony, Pet-Friendly"
              className={`w-full px-3 py-2 border ${errors.amenities ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm
                               focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                               transition-all duration-200 text-gray-800 placeholder-gray-400 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500`}
            />
            {renderError('amenities')}
          </div>
        </section>

        {/* Location Details Section */}
        <section className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-xl border border-gray-200 shadow-lg dark:from-gray-800 dark:to-gray-900 dark:border-gray-700 dark:shadow-none mx-0 sm:mx-auto sm:max-w-6xl">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 dark:text-white">Address Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
            {[
              { name: 'address', label: 'Street Address', placeholder: 'e.g., Plot No. 123, Road No. 45', required: true },
              { name: 'city', label: 'City', placeholder: 'e.g., Hyderabad', required: true },
              { name: 'state', label: 'State', placeholder: 'e.g., Telangana', required: true },
              { name: 'lat', label: 'Latitude', type: 'number', placeholder: 'e.g., 17.3850', min: "-90", max: "90", step: "any" },
              { name: 'lng', label: 'Longitude', type: 'number', placeholder: 'e.g., 78.4867', min: "-180", max: "180", step: "any" }
            ].map(({ name, label, type = 'text', placeholder, min, max, required, step }) => (
              <div key={name}>
                <label htmlFor={name} className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">
                  {label} {required && <span className="text-red-500">*</span>}
                </label>
                <input
                  id={name}
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  min={min}
                  max={max}
                  step={step}
                  className={`w-full px-3 py-2 border ${errors[name] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm
                                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                                  transition-all duration-200 text-gray-800 placeholder-gray-400 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500`}
                  required={required}
                />
                {renderError(name)}
              </div>
            ))}
          </div>
        </section>

        {/* Image Upload Section */}
        <section className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-xl border border-gray-200 shadow-lg dark:from-gray-800 dark:to-gray-900 dark:border-gray-700 dark:shadow-none mx-0 sm:mx-auto sm:max-w-6xl">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6 dark:text-white">Property Images <span className="text-red-500">*</span></h3>
          <div
            className={`border-2 ${isUploadingImages ? 'border-primary-400' : (errors.images ? 'border-red-500' : 'border-dashed border-gray-300 dark:border-gray-600')}
                               rounded-xl p-6 sm:p-8 text-center cursor-pointer transition-all duration-200
                               hover:border-primary-500 hover:bg-primary-50 group relative dark:hover:bg-primary-900`}
          >
            <label htmlFor="image-upload" className="cursor-pointer block py-2 sm:py-4">
              <div className="flex flex-col items-center justify-center">
                {isUploadingImages ? (
                  <Loader2 className="animate-spin h-8 w-8 sm:h-10 sm:w-10 text-primary-600 mb-3 sm:mb-4" />
                ) : (
                  <ImageIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-500 group-hover:text-primary-600 transition-colors mb-3 sm:mb-4 dark:text-gray-400 dark:group-hover:text-primary-300" />
                )}
                <span className="text-base sm:text-xl font-medium text-gray-700 group-hover:text-primary-800 transition-colors dark:text-gray-300 dark:group-hover:text-primary-200">
                  {isUploadingImages ? 'Uploading images...' : 'Click to upload or drag & drop images here'}
                </span>
                <p className="text-xs sm:text-sm text-gray-500 mt-2 dark:text-gray-400">Maximum file size: 5MB per image. Accepted formats: JPG, PNG, GIF.</p>
              </div>
            </label>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              className="hidden"
              disabled={isUploadingImages}
              ref={fileInputRef}
            />
          </div>
          {renderError('images')}


          {/* Image Previews */}
          {formData.images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 mt-6 sm:mt-8">
              {formData.images.map((image, index) => (
                <div key={image.url + index} className="relative w-full h-32 sm:h-36 rounded-lg overflow-hidden shadow-md group">
                  <img
                    src={image.url}
                    alt={`Property image ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  />
                  {image.isLocal && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-xs sm:text-sm font-semibold dark:bg-gray-900 dark:bg-opacity-70">
                      <Loader2 className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                      Uploading...
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 sm:p-1.5 shadow-lg
                                  hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                                  opacity-0 group-hover:opacity-100 transition-opacity duration-200 transform group-hover:scale-110"
                    aria-label={`Remove image ${index + 1}`}
                  >
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 sm:gap-5 pt-6 sm:pt-8 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-0">
          <button
            type="button"
            onClick={() => navigate('/aadashmin/properties')}
            className="px-6 py-2.5 sm:px-8 sm:py-3 text-base sm:text-lg font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm
                               hover:bg-gray-50 hover:border-gray-400 transition-colors duration-200
                               focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 dark:hover:border-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isUploadingImages}
            className="px-8 py-2.5 sm:px-10 sm:py-3 text-base sm:text-lg font-semibold text-white bg-primary-600 rounded-lg shadow-md
                               hover:bg-primary-700 transition-colors duration-200
                               disabled:opacity-60 disabled:cursor-not-allowed
                               focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Saving...
              </span>
            ) : (
              id ? 'Update Property' : 'Create Property'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PropertyForm;